import { useDispatch, useSelector } from "react-redux";

// rtk
import { uploadsSlice } from "../slices/uploadsSlice";

// utils
import { enhanceUploads } from "./utils";

/**
 * preserveMissingFields
 * Peserve any RTK props that are missing from API response
 *
 * Why? There are public api calls that are shared between UPL and MOD PHOTO
 * We are avoiding adding private props to the public api calls to remove the chance of exposing to public
 * Therefore, we cannot trust the backend public reponses to contain the full, MOD PHOTO upload object props
 * and must preserve props when missing
 * @param {object} incoming API response version of upload
 * @param {object} existing Redux State version of upload
 * @param {array} fieldsToPreserve props we want to preserve from Redux state if missing from updates
 * @returns {object} merged upload
 */
const preserveMissingFields = (incoming, existing, fieldsToPreserve) => {
    return fieldsToPreserve.reduce(
        (merged, key) => {
            const hasIncoming = Object.hasOwn(incoming, key);
            merged[key] = hasIncoming ? incoming[key] : existing?.[key];
            return merged;
        },
        { ...incoming }
    );
};

// frontend/components/fort/mod/photos/gql/fragments/photoUpload.js
// Add any fields in PHOTO_UPLOAD_TYPE_FRAGMENT that are not in PHOTO_UPLOAD_WITH_MOD_FRAGMENT
const fieldsToPreserve = [
    "photoTitleApproved",
    "photoDescriptionApproved",
    "isGem",
    "mature",
    "autoNudity",
    "autoViolence",
    "autoWarningText",
    "editorialUseOnlyApproved",
    "moderationNotes"
];

export const useApiUploadUpdate = () => {
    const dispatch = useDispatch();
    const { entities: batchEntities } = useSelector(state => state.batches);
    const { entities: uploadEntities } = useSelector(state => state.uploads);
    const { id: selectedBatchId } = useSelector(state => state.selectedBatch);
    const batchEntity = batchEntities[selectedBatchId];

    const apiUploadUpdate = ssResp => {
        // Payload should have photoUploads
        let uploads = ssResp?.data?.payload?.photoUploads || [];

        // extract `photoUpload` from response objects array
        uploads = uploads.map(({ photoUpload }) => {
            return { ...photoUpload };
        });

        // preserve missing moderation fields from existing redux state
        uploads = uploads.map(u => {
            const uploadEntity = uploadEntities[u.id] || {};
            return preserveMissingFields(u, uploadEntity, fieldsToPreserve);
        });

        // enhance uploads
        uploads = enhanceUploads(uploads, batchEntity);

        // convert to RTK Entity Adapter Update Objects
        const uploadUpdateEntityObjects = uploads.map(u => {
            const { id, ...rest } = u;
            const existing = uploadEntities[id] || {};

            return {
                id,
                changes: {
                    ...existing,
                    ...rest // overrides preserved props with API ones
                }
            };
        });

        // Update redux store
        dispatch(uploadsSlice.actions.updateUploads(uploadUpdateEntityObjects));
    };

    return {
        apiUploadUpdate
    };
};
